import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Trash2, Download, Eye, X, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { VERIF_LABEL, VERIF_COLOR } from "./common";

export default function IdReview({ booking, onClose, onChange }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/bookings/${booking.id}/id-document`).then((r) => setMeta(r.data)).catch(() => setMeta(null)).finally(() => setLoading(false));
  }, [booking.id]);
  useEffect(() => { load(); }, [load]);

  const viewFile = async (download) => {
    try {
      const r = await api.get(`/admin/bookings/${booking.id}/id-document/file${download ? "?download=1" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = meta?.document?.filename || "id"; a.click(); }
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Unable to open document"); }
  };
  const act = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(`/admin/bookings/${booking.id}/${path}`, body); toast.success(msg); onChange(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const requestId = () => act("request-id", {}, "ID upload request emailed to guest");
  const del = async () => {
    if (!window.confirm("Permanently delete this ID document?")) return;
    setBusy(true);
    try { await api.delete(`/admin/bookings/${booking.id}/id-document`); toast.success("ID document deleted"); onChange(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const vs = meta?.verification_status;
  const dstatus = meta?.document?.status;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="id-review-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-forest">ID Verification — {booking.guest_name}</h3>
          <button onClick={onClose} data-testid="id-review-close" className="text-stone-400 hover:text-forest"><X size={20} /></button>
        </div>
        {loading ? <Loader2 className="animate-spin mx-auto text-rose" /> : !meta ? (
          <div>
            <p className="text-sm text-stone-500 mb-4">No ID document has been uploaded for this booking yet. You can email the guest a secure link to upload it, or override the requirement.</p>
            <button onClick={requestId} disabled={busy} data-testid="id-request-upload" className="w-full text-sm py-2.5 rounded-full bg-rose text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5 mb-3"><Send size={14} /> Send ID upload request to guest</button>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required to override & confirm)" data-testid="id-reason" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm mb-3" rows={2} />
            <button onClick={() => act("override", { reason }, "Verification overridden — booking confirmed")} disabled={busy} data-testid="id-override" className="w-full text-sm py-2.5 rounded-full bg-forest text-cream font-semibold disabled:opacity-60">Override & confirm booking</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${VERIF_COLOR[vs] || "bg-stone-100 text-stone-600"}`} data-testid="id-review-status">{VERIF_LABEL[vs] || vs}</span>
              <span className="text-xs text-stone-400">{meta.document.filename} · {meta.document.content_type}</span>
            </div>
            {["deleted", "expired"].includes(dstatus) ? (
              <p className="text-sm text-stone-500 mb-4">This document has been {dstatus} and is no longer available.</p>
            ) : (
              <div className="flex gap-2 mb-4">
                <button onClick={() => viewFile(false)} data-testid="id-view" className="btn-dark text-xs py-2 px-3"><Eye size={14} /> View</button>
                <button onClick={() => viewFile(true)} data-testid="id-download" className="btn-dark text-xs py-2 px-3"><Download size={14} /> Download</button>
                <button onClick={del} disabled={busy} data-testid="id-delete" className="text-xs py-2 px-3 rounded-md bg-red-50 text-red-600 font-semibold inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>
              </div>
            )}
            {meta.document.reason && <p className="text-xs text-amber-700 mb-2">Reason on file: {meta.document.reason}</p>}
            {meta.override_reason && <p className="text-xs text-blue-700 mb-2">Override: {meta.override_reason} ({meta.override_by})</p>}
            <p className="text-[11px] text-stone-400 mb-3">Downloaded/viewed IDs are watermarked and every access is audit-logged.</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required to reject, request new or override)" data-testid="id-reason" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm mb-3" rows={2} />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => act("verify", { action: "approve" }, "ID approved")} disabled={busy} data-testid="id-approve" className="btn-primary text-xs py-2 justify-center">Approve</button>
              <button onClick={() => act("verify", { action: "reject", reason }, "ID rejected")} disabled={busy} data-testid="id-reject" className="text-xs py-2 rounded-full bg-red-50 text-red-600 font-semibold">Reject</button>
              <button onClick={() => act("verify", { action: "request_new", reason }, "New ID requested")} disabled={busy} data-testid="id-request-new" className="text-xs py-2 rounded-full bg-amber-50 text-amber-700 font-semibold">Request resubmission</button>
              <button onClick={() => act("override", { reason }, "Verification overridden — booking confirmed")} disabled={busy} data-testid="id-override" className="text-xs py-2 rounded-full bg-forest text-cream font-semibold">Override & confirm</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
